/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import Dates from 'utils/Dates'
import dayjs from 'dayjs'
import React, {useCallback, useMemo} from 'react'
import {TFunction} from 'i18next'
import {Lead} from 'types/entities/Lead'
import LeadsAction from 'store/leads/LeadsAction'
import {closeModal, openModal} from 'helpers/actions'
import EditLeadModal from 'components/leads/EditLeadModal'
import {ModalSize} from 'types/enums/ModalSize'
import {useTranslation} from 'react-i18next'
import {useAppDispatch} from 'helpers/store'
import {LeadCategory} from 'types/enums/LeadCategory'
import {LeadStatus} from 'types/enums/LeadStatus'
import {LeadSource} from 'types/enums/LeadSource'
import {EditLeadModalTab} from 'types/enums/specific/EditLeadModalTab'
import {CompanyBranchType} from 'types/enums/CompanyBranchType'
import {toast} from 'utils/toastUtils'
import {getCompanyDisplayName} from 'utils/companyUtils'
import {toSearchFilterList} from 'utils/utility'
import { formatFilterDate, parseFilterDate } from 'utils/dateRangePresets'

/* --- Lead Status (Added to support UiLeadStatusSelect) --- */

export interface LeadStatusDescriptor {
    value: LeadStatus
    label: string
    description?: string
}

export const getLeadStatusDescriptors = (t: TFunction): LeadStatusDescriptor[] => [
    { value: LeadStatus.New, label: t('Neu'), description: t('New created') },
    { value: LeadStatus.ContactAttempt, label: t('Kontaktversuch'), description: t('Contact attempt') },
    { value: LeadStatus.Contacted, label: t('In Kontakt'), description: t('In Contact') },
    { value: LeadStatus.Qualified, label: t('Qualifiziert'), description: t('Qualified lead') },
    {
        value: LeadStatus.OpportunityCreated,
        label: t('Opportunity erstellen'),
        description: t('Opportunity has been created'),
    },
    { value: LeadStatus.Disqualified, label: t('Disqualifiziert'), description: t('Disqualified lead') },
    // { value: LeadStatus.Negotiation, label: t('Negotiation'), description: t('In negotiation') },
    // { value: LeadStatus.Won, label: t('Won'), description: t('Successfully closed') },
    // { value: LeadStatus.Lost, label: t('Lost'), description: t('Lost') },
]

export const getOpportunityStatusDescriptors = (t: TFunction): LeadStatusDescriptor[] => [
    { value: LeadStatus.NewInquiry, label: t('Neue Anfrage'), description: t('New inquiry received') },
    { value: LeadStatus.IndicativeOffer, label: t('Richtangebot'), description: t('Indicative offer sent') },
    { value: LeadStatus.Quotation, label: t('Offerte'), description: t('Quotation provided') },
    { value: LeadStatus.OnHold, label: t('On Hold'), description: t('On hold') },
    { value: LeadStatus.Acceptance, label: t('Zusage'), description: t('Offer accepted') },
    { value: LeadStatus.Rejection, label: t('Absage'), description: t('Rejected') },
    { value: LeadStatus.OnlineBooked, label: t('Online Booked'), description: t('Online booking from customer') },
    { value: LeadStatus.Finished, label: t('Completed'), description: t('Offer finished with a reservation') },
]

/** Offerte / Zusage / Abgeschlossen — set by system flows only, not manual UI. */
export const SYSTEM_ONLY_OPPORTUNITY_STATUSES = new Set<LeadStatus>([
    LeadStatus.Quotation,
    LeadStatus.Acceptance,
    LeadStatus.Finished,
])

export const isManuallySelectableOpportunityStatus = (
    status: LeadStatus,
    currentStatus?: LeadStatus | null
): boolean => {
    if (!SYSTEM_ONLY_OPPORTUNITY_STATUSES.has(status)) {
        return true
    }
    return currentStatus === status
}

export const getSystemOnlyOpportunityStatusReason = (t: TFunction, status: LeadStatus): string => {
    if (status === LeadStatus.Acceptance) {
        return t('Accepted status can only be set by creating a booking from an offer.') as string
    }
    if (status === LeadStatus.Quotation) {
        return t('Offerte status can only be set by creating an offer.') as string
    }
    if (status === LeadStatus.Finished) {
        return t('Completed status can only be set by the system.') as string
    }
    return t('This status cannot be set manually.') as string
}

export const getAllStatusDescriptors = (t: TFunction): LeadStatusDescriptor[] => [
    ...getLeadStatusDescriptors(t),
    ...getOpportunityStatusDescriptors(t),
]

const SOURCE_LABEL_MAP: Record<LeadSource, string> = {
    [LeadSource.EventForm]: 'Event inquiry form',
    [LeadSource.Mail]: 'Email inquiry',
    [LeadSource.Phone]: 'Phone inquiry',
    [LeadSource.Marketing]: 'Marketing & campaigns',
    [LeadSource.Sales]: 'Sales activities & network',
    [LeadSource.Existing]: 'Existing customer',
    [LeadSource.Referral]: 'Recommendation & referral',
    [LeadSource.Other]: 'Other',
}

export const getLeadSourceDescriptors = (t: TFunction): { value: LeadSource; label: string }[] =>
    Object.values(LeadSource).map((value) => ({
        value,
        label: t(SOURCE_LABEL_MAP[value]),
    }))

export const useLeadSourceOptions = () => {
    const { t } = useTranslation()

    const options = useMemo(
        () =>
            getLeadSourceDescriptors(t).map((descriptor) => ({
                label: descriptor.label,
                value: descriptor.value,
            })),
        [t]
    )

    return { options }
}

export const getLeadSourceLabel = (source: string | null | undefined, t: TFunction): string => {
    if (!source) return '-'
    const key = SOURCE_LABEL_MAP[source as LeadSource]
    return key ? t(key) : source
}

export const useLeadStatusOptions = (isOpportunity: boolean = false) => {
    const { t } = useTranslation()

    const statusDescriptors = isOpportunity ? getOpportunityStatusDescriptors(t) : getLeadStatusDescriptors(t)

    const allDescriptors = getAllStatusDescriptors(t)

    const options = useMemo(
        () =>
            statusDescriptors.map((d) => ({
                label: d.label,
                description: d.description,
                value: d.value,
            })),
        [statusDescriptors]
    )

    const map = useMemo(() => {
        const m = {} as Record<LeadStatus, LeadStatusDescriptor>
        allDescriptors.forEach((d) => {
            m[d.value] = d
        })
        return m
    }, [allDescriptors])

    const getStatusDescriptor = (value: LeadStatus) => map[value] ?? map[LeadStatus.New]

    return { options, map, getStatusDescriptor }
}

/* --- Lead Category Helpers (Added to support UiLeadCategorySelect while keeping useGetLeadCategory) --- */

export const useLeadCategoryOptions = () => {
    const { getLeadCategoryDescriptors, getLeadCategoryByValue } = useGetLeadCategory()

    const options = useMemo(
        () =>
            getLeadCategoryDescriptors.map((d) => ({
                label: d.label,
                description: d.description,
                value: d.value,
            })),
        [getLeadCategoryDescriptors]
    )

    const map = useMemo(() => {
        const m = {} as Record<LeadCategory, LeadCategoryDescriptor>
        getLeadCategoryDescriptors.forEach((d) => {
            m[d.value] = d
        })
        return m
    }, [getLeadCategoryDescriptors])

    const getCategoryDescriptor = (value: LeadCategory) => getLeadCategoryByValue(value)

    return { options, map, getCategoryDescriptor }
}

export interface LeadCategoryDescriptor {
    value: LeadCategory
    label: string
    description: string
}

export const useGetLeadCategory = () => {
    const { t } = useTranslation()

    const getLeadCategoryDescriptors: LeadCategoryDescriptor[] = [
        {
            value: LeadCategory.Cold,
            label: t('Standard'),
            description: t('Low interest'),
        },

        {
            value: LeadCategory.Warm,
            label: t('High'),
            description: t('Interested in services'),
        },
        {
            value: LeadCategory.Hot,
            label: t('Key'),
            description: t('Very likely to close'),
        },
        // {
        //     value: LeadCategory.Lost,
        //     label: t('Lost'),
        //     description: t('Lost'),
        // },
        // {
        //     value: LeadCategory.Recycled,
        //     label: t('Recycled'),
        //     description: t('Future follow up'),
        // },
    ]

    const getLeadCategoryByValue = (value: LeadCategory): LeadCategoryDescriptor => {
        return (
            getLeadCategoryDescriptors.find((d) => d.value === value) ?? getLeadCategoryDescriptors[1] // Default to Warm if not found
        )
    }

    return {
        getLeadCategoryByValue,
        getLeadCategoryDescriptors,
    }
}

export const transformLeadsSearchFilters = (
    filters: { id: string; value: any }[] | { id: string; value: any } | undefined,
    SEARCH_MAP: Record<string, string[]>
): { id: string; value: any }[] => {
    const acc: { id: string; value: any }[] = []
    const list = toSearchFilterList(filters)

    const normalizeDateRangeValue = (value: unknown) => {
        if (!value || typeof value !== 'object' || Array.isArray(value)) {
            return value
        }
        const range = value as { from?: unknown; to?: unknown }
        if (!range.from || !range.to) {
            return value
        }
        const fromDate = parseFilterDate(String(range.from))
        const toDate = parseFilterDate(String(range.to))
        if (!fromDate || !toDate) {
            return value
        }
        return {
            from: formatFilterDate(fromDate),
            to: formatFilterDate(toDate),
        }
    }

    for (const f of list) {
        if (!f?.id) continue
        const id: string = f.id
        const fields = SEARCH_MAP[id]
        if (fields) {
            const normalizedValue = normalizeDateRangeValue(f.value)
            for (const field of fields) acc.push({ id: field, value: normalizedValue })
            continue
        }
        const isDateId =
            typeof id === 'string' &&
            (id.toLowerCase().includes('date') ||
                id.toLowerCase().includes('at') ||
                id.toLowerCase().includes('time'))

        if (isDateId) {
            const v = f.value
            if (v && typeof v === 'object' && v.from && v.to) {
                acc.push({
                    id,
                    value: normalizeDateRangeValue(v),
                })
                continue
            }

            const isArray = Array.isArray(v) && v.length === 2
            if (!isArray && v) {
                const date = typeof v === 'string' || v instanceof Date ? new Date(v) : null
                if (date && !isNaN(date.getTime())) {
                    const range = Dates.getStartAndEndOfWeek(date)
                    acc.push({
                        id,
                        value: {
                            from: range.startOfWeek,
                            to: range.endOfWeek,
                        },
                    })
                    continue
                }
            }
        }
        acc.push(f)
    }
    return acc
}

/** Maps UI column ids to API sort field ids for POST /admin/leads/table. */
export const LEADS_TABLE_SORT_MAP: Record<string, string> = {
    seller_id: 'seller.name',
    company: 'company.name',
    company_branche: 'company.business_category',
    company_state: 'company.address.region',
    company_type: 'company.type',
    employee_size: 'company.employee_size',
    contact_position: 'contact.position',
    contact_gender: 'contact.gender',
    contact: 'contact_person',
}

export const transformLeadsSorting = (
    sorting: { id: string; desc: boolean }[]
): { id: string; desc: boolean }[] =>
    sorting.map((sort) => ({
        ...sort,
        id: LEADS_TABLE_SORT_MAP[sort.id] ?? sort.id,
    }))

export const useTransformLeadsSearch = (SEARCH_MAP: Record<string, string[]>) => {
    const transformSearch = useCallback(
        (filters: { id: string; value: any }[] | { id: string; value: any } | undefined) =>
            transformLeadsSearchFilters(filters, SEARCH_MAP),
        [SEARCH_MAP]
    )
    return {
        transformSearch,
    }
}

export const useLeadActions = () => {
    const dispatch = useAppDispatch()
    const { t } = useTranslation()

    const openEditLeadModal = (lead: Lead, onSuccess: any, initialTab?: EditLeadModalTab) => {
        dispatch(LeadsAction.fetchLeadById(lead)).then((loadedLead: Lead) =>
            dispatch(
                openModal(
                    <EditLeadModal lead={loadedLead} onSuccess={onSuccess} initialTab={initialTab} />,
                    {
                        modalSize: ModalSize.RoundedFullscreen,
                        closeModal: () => {
                            dispatch(LeadsAction.resetSelectedLead())
                            dispatch(closeModal())
                        },
                        onError: () => {
                            toast.error(t('An error occurred'))
                        },
                    },
                    loadedLead.category === LeadCategory.Opportunity ? t('Edit Opportunity') : t('Edit Lead')
                )
            )
        )
    }

    return {
        openEditLeadModal,
    }
}

export const parseLeadRequestedDateTime = (
    requestedDate?: string | null,
    requestedTime?: string | null
) => {
    if (!requestedDate) {
        return null
    }

    const dateValue = dayjs(requestedDate)
    if (!dateValue.isValid()) {
        return null
    }

    if (!requestedTime) {
        return dateValue
    }

    const timeParts = requestedTime.trim().split(':')
    const hours = parseInt(timeParts[0] ?? '', 10)
    const minutes = parseInt(timeParts[1] ?? '', 10)

    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
        return dateValue
    }

    return dateValue.hour(hours).minute(minutes).second(0).millisecond(0)
}

export const formatLeadRequestedDateTime = (
    requestedDate?: string | null,
    requestedTime?: string | null
): string => {
    const parsed = parseLeadRequestedDateTime(requestedDate, requestedTime)
    if (!parsed) {
        return '-'
    }

    const hasExplicitTime =
        Boolean(requestedTime?.trim()) || parsed.hour() !== 0 || parsed.minute() !== 0

    if (hasExplicitTime) {
        return Dates.dateTime(parsed) || parsed.format('DD.MM.YYYY HH:mm')
    }

    return Dates.date(parsed) || '-'
}

export const getLeadDisplayName = (lead: Lead, t: TFunction) => {
    return (
        lead.name ||
        lead.company_name ||
        lead.full_name ||
        (lead.contact ? lead.contact.first_name + ' ' + lead.contact.last_name : t('No name'))
    )
}

export const getLeadCompanyDisplayName = (lead: Lead): string => {
    const companyContactName = lead.contact
        ? `${lead.contact.first_name || ''} ${lead.contact.last_name || ''}`.trim()
        : ''
    const leadContact = (lead as Lead & { contact?: { first_name?: string; last_name?: string } }).contact
    const leadContactName = leadContact
        ? `${leadContact.first_name || ''} ${leadContact.last_name || ''}`.trim()
        : ''

    if (lead.company?.type === CompanyBranchType.Private) {
        return (
            lead.contact_person ||
            lead.full_name ||
            leadContactName ||
            companyContactName ||
            getCompanyDisplayName(lead.company) ||
            lead.name ||
            ''
        )
    }

    return (
        getCompanyDisplayName(lead.company) ||
        companyContactName ||
        lead.company_name ||
        ''
    )
}

export const getLeadContactPerson = (lead: Lead, t: TFunction) => {
    return lead.contact_person || t('No contact person')
}

export const getLeadEmail = (lead: Lead, t: TFunction) => {
    return lead.email || t('No email')
}

export const getLeadPhone = (lead: Lead, t: TFunction) => {
    return lead.phone || t('No phone')
}

export const getLeadLink = (leadId: number | string) => {
    return `/leads/${leadId}`
}

export const isLeadOpportunity = (lead: Lead) => {
    return lead.category === LeadCategory.Opportunity
}

